import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Link as LinkIcon } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { selectIsAuthenticated } from '@/features/auth/authSlice';
import { Login } from '../auth/Login';

interface Creator30ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    websiteLinks: string[];
    communicationMethod: string;
    comment: string;
  }) => void;
}

const COMMUNICATION_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'messenger', label: 'Messenger' },
];

export const Creator30Modal = ({ isOpen, onClose, onSubmit }: Creator30ModalProps) => {
  const { t } = useTranslation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [websiteLinks, setWebsiteLinks] = useState<string[]>(['']);
  const [communicationMethod, setCommunicationMethod] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleAddLink = () => setWebsiteLinks([...websiteLinks, '']);
  const handleRemoveLink = (index: number) => {
    setWebsiteLinks(websiteLinks.filter((_, i) => i !== index));
  };
  const handleLinkChange = (index: number, value: string) => {
    const newLinks = [...websiteLinks];
    newLinks[index] = value;
    setWebsiteLinks(newLinks);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const validWebsiteLinks = websiteLinks.filter(link => link.trim() !== '');

    if (validWebsiteLinks.length === 0) {
      alert(t('discountPrograms.errors.artworkLinksRequired', 'At least one artwork link is required'));
      setIsSubmitting(false);
      return;
    }

    try {
      await onSubmit({
        websiteLinks: validWebsiteLinks,
        communicationMethod,
        comment,
      });
      onClose();
    } catch (error) {
      console.error('Failed to submit application:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg bg-background p-6 shadow-lg">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1 hover:bg-muted"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-xl font-semibold">
          {t('discountPrograms.creator30.modalTitle', 'Apply for Creator Elite (30%)')}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('discountPrograms.creator30.modalDescription', 'For established creators with websites or online shops.')}
        </p>

        {!isAuthenticated ? (
          <div className="mt-6">
            <Login 
              standalone 
              initialMode="login" 
              onSuccess={() => {}} 
            />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* Artwork Links */}
          <div className="space-y-3">
            <label className="text-sm font-medium">
              {t('discountPrograms.form.artworkLinks', 'Shop or Portfolio Links')}
              <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-muted-foreground">
              {t('discountPrograms.form.artworkLinksDescription', 'Provide links to your website, shop, or online portfolio (at least one required)')}
            </p>

            {websiteLinks.map((link, index) => (
              <div key={index} className="flex gap-2">
                <div className="relative flex-1">
                  <LinkIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => handleLinkChange(index, e.target.value)}
                    placeholder={t('discountPrograms.form.websiteLinkPlaceholder', 'https://yourshop.com')}
                    className="w-full rounded-md border bg-background pl-9 pr-3 py-2 text-sm"
                    required={index === 0}
                  />
                </div>
                {websiteLinks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveLink(index)}
                    className="rounded-md border px-3 py-2 text-sm hover:bg-muted"
                  >
                    {t('common.remove', 'Remove')}
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddLink}
              className="text-sm text-primary hover:underline"
            >
              + {t('discountPrograms.form.addAnotherLink', 'Add another link')}
            </button>
          </div>

          {/* Communication Method */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('discountPrograms.form.communicationMethod', 'Preferred Communication Method')}
            </label>
            <select
              value={communicationMethod}
              onChange={(e) => setCommunicationMethod(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              required
            >
              <option value="">{t('common.select', 'Select...')}</option>
              {COMMUNICATION_METHODS.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('discountPrograms.form.comment', 'Additional Comments')}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              placeholder={t('discountPrograms.form.commentPlaceholder30', 'Tell us about your business...')}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background hover:bg-foreground/90 disabled:opacity-50"
            >
              {isSubmitting
                ? t('common.submitting', 'Submitting...')
                : t('common.submitApplication', 'Submit Application')}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};
